import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Info, Swords, Trophy } from "lucide-react";
import type { PenaltyMatchDto } from "@/types/api";
import { matchesApi } from "@/api/matches";
import { penaltyApi } from "@/api/penalty";
import { useScope } from "@/app/useScope";
import { useMatchActions } from "@/app/useMatchActions";
import { MatchDayBoard } from "@/components/MatchDayBoard";
import { TeamFlag } from "@/components/TeamFlag";
import { PenaltyPicksModal } from "@/components/PenaltyPicksModal";
import { RequireContext, EmptyState, ErrorState, CardListSkeleton } from "@/components/StateViews";
import { Tabs } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { isGroupStage, stageLabel, formatTime } from "@/lib/stage";

export function KnockoutPage() {
  return (
    <RequireContext>
      <Knockout />
    </RequireContext>
  );
}

function Knockout() {
  const [tab, setTab] = useState("matches");
  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <Swords className="size-5 text-primary" /> Kieséses szakasz
        </h1>
        <p className="text-sm text-muted-foreground">Tippelj a kieséses mérkőzésekre, és a döntetlennel záruló meccsek tizenegyes-győztesére.</p>
      </div>

      <Tabs
        items={[
          { value: "matches", label: "Mérkőzések" },
          { value: "penalty", label: "Tizenegyes párbaj" },
        ]}
        value={tab}
        onValueChange={setTab}
      />

      {tab === "matches" ? <KnockoutMatches /> : <PenaltySection />}
    </div>
  );
}

function KnockoutMatches() {
  const scope = useScope();
  const q = useQuery({
    queryKey: ["matches", scope.leagueId, scope.tournamentId],
    queryFn: () => matchesApi.list(scope),
  });

  const koMatches = (q.data ?? []).filter((m) => !isGroupStage(m.stage));

  if (q.isLoading) return <CardListSkeleton />;
  if (q.error) return <ErrorState error={q.error} />;
  if (koMatches.length === 0)
    return <EmptyState title="Még nincs kieséses szakasz" description="A kieséses meccsek a csoportkör után jelennek meg." icon={<Swords className="size-7" />} />;

  return <MatchDayBoard matches={koMatches} showStage />;
}

function PenaltySection() {
  const scope = useScope();
  const { submitPenalty } = useMatchActions();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [picksMatchId, setPicksMatchId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["penalty", scope.leagueId, scope.tournamentId],
    queryFn: () => penaltyApi.get(scope),
  });

  async function pick(matchId: string, teamId: string) {
    setBusyId(matchId + teamId);
    try {
      await submitPenalty(matchId, teamId);
    } finally {
      setBusyId(null);
    }
  }

  if (q.isLoading) return <CardListSkeleton count={2} />;
  if (q.error) return <ErrorState error={q.error} />;
  const matches = q.data?.matches ?? [];
  if (matches.length === 0)
    return (
      <EmptyState
        title="Nincs tizenegyes-párbaj meccs"
        description="Csak a döntetlennel záruló kieséses meccseknél kell a tizenegyes-győztest tippelni."
      />
    );

  return (
    <div className="space-y-3">
      {q.data && q.data.bonusPoints > 0 && (
        <p className="text-sm text-muted-foreground">
          Eddigi tizenegyes-bónuszod: <span className="font-semibold text-primary">{q.data.bonusPoints} pont</span>
        </p>
      )}
      <div className="grid gap-3 lg:grid-cols-2">
        {matches.map((m) => (
          <PenaltyCard key={m.matchId} m={m} busyId={busyId} onPick={pick} onOpenPicks={setPicksMatchId} />
        ))}
      </div>
      <PenaltyPicksModal matchId={picksMatchId} onClose={() => setPicksMatchId(null)} />
    </div>
  );
}

function PenaltyCard({
  m,
  busyId,
  onPick,
  onOpenPicks,
}: {
  m: PenaltyMatchDto;
  busyId: string | null;
  onPick: (matchId: string, teamId: string) => void;
  onOpenPicks: (matchId: string) => void;
}) {
  const resolved = !!m.penaltyWinnerTeamId;
  const correct = resolved && m.myPickedTeamId === m.penaltyWinnerTeamId;
  const disabled = m.locked || resolved || busyId !== null;

  return (
    <div
      className={cn(
        "rounded-xl border bg-card p-3 shadow-sm sm:p-4",
        resolved
          ? correct
            ? "border-warning ring-1 ring-warning/40"
            : "border-destructive ring-1 ring-destructive/30"
          : "border-border",
      )}
    >
      <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="outline">{stageLabel(m.stage)}</Badge>
        <span className="tabular-nums">{formatTime(m.matchDate)}</span>
        {m.matchHasResult && !m.matchWasDraw && <span>Nem volt döntetlen</span>}
        <div className="ml-auto flex items-center gap-1.5">
          {m.bonusAwarded && <Badge variant="warning">Bónusz</Badge>}
          {resolved && (
            <button
              type="button"
              onClick={() => onOpenPicks(m.matchId)}
              className="rounded-full p-1 hover:bg-accent"
              title="Mások tippjei"
              aria-label="Mások tippjei"
            >
              <Info className="size-4" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <TeamPick
          name={m.teamAName}
          flag={m.teamAFlag}
          pickedByMe={m.myPickedTeamId === m.teamAId}
          isWinner={m.penaltyWinnerTeamId === m.teamAId}
          resolved={resolved}
          disabled={disabled}
          onClick={() => onPick(m.matchId, m.teamAId)}
        />
        <TeamPick
          name={m.teamBName}
          flag={m.teamBFlag}
          pickedByMe={m.myPickedTeamId === m.teamBId}
          isWinner={m.penaltyWinnerTeamId === m.teamBId}
          resolved={resolved}
          disabled={disabled}
          onClick={() => onPick(m.matchId, m.teamBId)}
        />
      </div>

      {m.locked && !resolved && <p className="mt-2 text-center text-xs text-muted-foreground">Lezárva — a győztes még nincs rögzítve</p>}
      {resolved && (
        <p className={cn("mt-2 text-center text-xs font-medium", correct ? "text-warning" : m.myPickedTeamId ? "text-destructive" : "text-muted-foreground")}>
          {correct ? "🎯 Eltaláltad a párbajgyőztest!" : m.myPickedTeamId ? "Nem talált" : "Nem tippeltél erre"}
        </p>
      )}
    </div>
  );
}

function TeamPick({
  name,
  flag,
  pickedByMe,
  isWinner,
  resolved,
  disabled,
  onClick,
}: {
  name: string;
  flag: string | null;
  pickedByMe: boolean;
  isWinner: boolean;
  resolved: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex items-center gap-2 rounded-lg border p-2.5 text-left text-sm font-medium disabled:cursor-not-allowed",
        isWinner
          ? "border-success bg-success/10 text-success"
          : pickedByMe && !resolved
            ? "border-primary bg-primary/10 text-primary"
            : pickedByMe && resolved
              ? "border-destructive/40 text-destructive"
              : "border-border hover:border-primary/50 disabled:opacity-70",
      )}
    >
      <TeamFlag name={name} flag={flag} className="size-6" />
      <span className="min-w-0 flex-1 truncate">{name}</span>
      {isWinner && (
        <span className="inline-flex items-center gap-0.5 text-xs">
          <Trophy className="size-3.5" /> győztes
        </span>
      )}
    </button>
  );
}
